import { useMemo, useState } from "react";
import {
  Flex,
  Heading,
  View,
  Text,
  DialogTrigger,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import ContactIcon from "../../../assets/contactIcon.svg";
import Emitter from "../../../emitter";
import { API_CALL_DIAL, noActiveHistory } from "../../../constants/constants";
import storeRTK from "../../../store/storeRTK";
import { setSelectedTab } from "../../../actions/common";
import { RootState } from "../../../store/storeConfig";
import { fixJson, formatTimestamp, getInitialsAndColor } from "../../../utils";
import HistoryCallButton from "../../common/HistoryCallButton/HistoryCallButton";
import { formatLabel } from "../../../utils/utils";
import ActiveCallContainer from "../../CallTab/CallDialer/ActiveCallContainer";
import { useCustomerAttributes } from "../../../hooks/useCustomerAttributes";
import { AddToSpeedDialDialog } from "../../SpeedDial/AddToSpeedDialDialog/AddToSpeedDialDialog";
import { API_MILO_CASE_CHANNEL_TRANSACTION } from "../../../constants/events";
import { SpeedDialData } from "../../../types/generic.types";

type Props = {
  activeCallDetail: any;
  type: "call" | "callback" | "voicemail";
};

const HistoryDetail = ({ activeCallDetail, type }: Props) => {
  const agent = useSelector((state: RootState) => state.agent);
  const speedDials = useSelector(
    (state: RootState) => state.history.speedDials
  );
  const customerAttributes = activeCallDetail?.customer?.attributes || {};
  const agentBusinessAttributes = agent?.businessAttributes || [];
  const [isOpen, setIsOpen] = useState(false);

  const phoneNumber =
    customerAttributes?.PHONE_NUMBER ||
    activeCallDetail?.customer?.displayName ||
    activeCallDetail?.customer?.phoneNumber ||
    customerAttributes?.Customer_ANI ||
    "";

  // Clean phone number for speed dial (remove special characters except +)
  const cleanPhoneNumber = phoneNumber
    ? phoneNumber.replace(/[^\d+]/g, "").replace(/\+(\d)/g, "+$1")
    : "";

  // Ensure the number starts with + if it has digits
  const formattedCleanNumber =
    cleanPhoneNumber && !cleanPhoneNumber.startsWith("+")
      ? `+${cleanPhoneNumber}`
      : cleanPhoneNumber;

  // Check if phoneNumber exists in speed dial entries
  const speedDialEntry = speedDials?.find(
    (entry: SpeedDialData) => entry?.phoneNumber === formattedCleanNumber
  );
  const isPhoneInSpeedDial = !!speedDialEntry;

  // Derive full name without setting state during render
  const fullName = useMemo(() => {
    const quickConnectName = (activeCallDetail?.self as any)?.quickConnectName;
    if (quickConnectName) return quickConnectName;

    if (speedDialEntry) {
      const first = speedDialEntry.firstName || "N";
      const last = speedDialEntry.lastName || "A";
      return `${first} ${last}`.trim();
    }

    const first = customerAttributes?.FIRSTNAME || "";
    const last = customerAttributes?.LASTNAME || "";
    const candidate = `${first} ${last}`.trim();

    return candidate || "N A";
  }, [activeCallDetail?.id, speedDialEntry, customerAttributes]);

  const { initials, backgroundColor } = useMemo(() => {
    const source =
      (activeCallDetail?.self as any)?.quickConnectName || fullName || "N A";
    return getInitialsAndColor(source) as {
      initials: string;
      backgroundColor: string;
    };
  }, [activeCallDetail?.id, fullName]);

  const dialCall = () => {
    Emitter.emit(API_CALL_DIAL, { phoneNumber: formattedCleanNumber });
    storeRTK.dispatch(setSelectedTab("Call"));
  };

  const openMiloPopup = (caseId: string) => {
    Emitter.emit(API_MILO_CASE_CHANNEL_TRANSACTION, {
      type: "notif",
      chan: API_MILO_CASE_CHANNEL_TRANSACTION,
      body: {
        contactId: caseId,
      },
    });
  };

  const { groupedAttributes } = useCustomerAttributes({
    customerAttrs: customerAttributes,
    businessAttrs: agentBusinessAttributes,
    type: "detail",
  });

  const getCustomerPhone = () => {
    if ((activeCallDetail?.self as any)?.quickConnectName) return;
    else return phoneNumber || "-";
  };

  const hasCustomerName = () => {
    // Check if quickConnectName is available first
    if ((activeCallDetail?.self as any)?.quickConnectName) {
      return true;
    }

    // Check if there's a speed dial entry
    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return true;
    }

    // Fall back to existing logic
    return !!(customerAttributes?.FIRSTNAME && customerAttributes?.LASTNAME);
  };

  return activeCallDetail?.id ? (
    <View
      borderColor="gray-200"
      borderWidth="thin"
      height="100%"
      backgroundColor="gray-100"
      UNSAFE_className="call-history-detail"
    >
      <View
        padding="10px 15px"
        borderBottomWidth="thin"
        borderBottomColor="gray-200"
      >
        <Flex
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center">
            <Flex
              justifyContent="center"
              alignItems="center"
              UNSAFE_className="agent-avatar"
              UNSAFE_style={{ backgroundColor, borderRadius: "50%" }}
            >
              <Text UNSAFE_className="call-monitor-agent-text">{initials}</Text>
            </Flex>
            <View marginX={12}>
              <Heading level={3} margin={0}>
                <Text UNSAFE_className="call-history-detail-title">
                  {fullName === "N A" ? phoneNumber : fullName}
                </Text>
              </Heading>
              {(isPhoneInSpeedDial || hasCustomerName()) && (
                <Text UNSAFE_className="call-history-detail-phone-number">
                  {getCustomerPhone()}
                </Text>
              )}
            </View>
          </Flex>

          <Flex direction="row" alignItems="center" gap="size-200">
            <button
              className="call-history-action-btn"
              aria-label="Add to speed dials"
              data-testid="add-speed-dials-button"
              onClick={() => setIsOpen(true)}
            >
              <Flex alignItems="center">
                <img src={ContactIcon} alt="contact icon" />
                <Text marginX={7} UNSAFE_style={{ whiteSpace: "nowrap" }}>
                  {isPhoneInSpeedDial ? "Edit contact" : "Add to speed dials"}
                </Text>
              </Flex>
              {isOpen && (
                <DialogTrigger
                  type="modal"
                  isOpen={isOpen}
                  onOpenChange={setIsOpen}
                  isKeyboardDismissDisabled
                >
                  {/* Required trigger element */}
                  <span />
                  {(close) => (
                    <AddToSpeedDialDialog
                      onClose={() => {
                        setIsOpen(false);
                        close();
                      }}
                      phoneNumber={formattedCleanNumber}
                      firstName={
                        isPhoneInSpeedDial
                          ? speedDialEntry.firstName || ""
                          : customerAttributes?.FIRSTNAME || ""
                      }
                      lastName={
                        isPhoneInSpeedDial
                          ? speedDialEntry.lastName || ""
                          : customerAttributes?.LASTNAME || ""
                      }
                      contactType={
                        isPhoneInSpeedDial
                          ? speedDialEntry.contactType
                          : "Customer"
                      }
                      isEdit={isPhoneInSpeedDial}
                    />
                  )}
                </DialogTrigger>
              )}
            </button>
            <HistoryCallButton onClick={() => dialCall()} />
          </Flex>
        </Flex>
      </View>
      <View>
        <Flex direction="row" height="100%">
          <Flex width="100%">
            <View UNSAFE_className="call-history-detail-body" width="100%">
              {groupedAttributes.length > 0 ? (
                groupedAttributes.map((group, i) => (
                  <View key={`group-${i}`} marginTop={23}>
                    <Flex
                      direction="row"
                      wrap="wrap"
                      gap={20}
                      justifyContent="start"
                    >
                      {group.map((attr, index) => (
                        <Flex
                          direction="column"
                          key={`attr-${i + index}`}
                          UNSAFE_className="customer-attributes-column"
                        >
                          <Text UNSAFE_className="call-detail-title">
                            {attr.attributeName.toLowerCase() === "notes"
                              ? "Transfer Notes"
                              : formatLabel(attr.attributeName)}
                          </Text>
                          {formatLabel(attr.attributeName) ===
                          "Aic Web Contact Monitored History" ? (
                            <Text UNSAFE_className="call-detail-description">
                              {fixJson(
                                customerAttributes[attr.attributeName]
                              ).map((detail: any, idx: number) => (
                                <Flex
                                  justifyContent={"space-between"}
                                  marginTop={4}
                                  key={idx}
                                >
                                  <Text>{detail.user}</Text>
                                  <Text>{formatTimestamp(detail.TS)}</Text>
                                </Flex>
                              ))}
                            </Text>
                          ) : formatLabel(attr.attributeName).toLowerCase() ===
                            "caseid" ? (
                            <a
                              className="case-id-link"
                              onClick={() =>
                                openMiloPopup(
                                  customerAttributes["AIC_CURRENT_CONTACT_ID"]
                                )
                              }
                              style={{
                                cursor: "pointer",
                                color: "#1473E6",
                                textDecoration: "underline",
                                wordBreak: "break-word",
                              }}
                            >
                              {customerAttributes[attr.attributeName]}
                            </a>
                          ) : (
                            <Text UNSAFE_className="call-detail-description">
                              {customerAttributes[attr.attributeName]}
                            </Text>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                  </View>
                ))
              ) : (
                <Flex marginTop={23} justifyContent="center">
                  <Text>No Detail Found</Text>
                </Flex>
              )}
            </View>
          </Flex>
        </Flex>
      </View>
    </View>
  ) : (
    <Flex alignItems="center" justifyContent="center" height="60%" width="100%">
      <ActiveCallContainer
        title={noActiveHistory[type].title}
        description={noActiveHistory[type].description}
      />
    </Flex>
  );
};

export default HistoryDetail;
